import api, { apiFormData } from "../../../utils/api";
import { unwrap } from "./apiEnvelope";
import { fetchCrmObjects, updateCrmObject } from "./crmObjectsService";
import type { Quote } from "./quotesAnalytics";
import type { QuoteItem } from "./quoteMath";

// ============================================================================
// Cotizaciones — data access for the tracking screen. The quotes themselves go
// through the generic CRM objects API (`type=quotes`); the document attached to
// a quote has its own routes (/crm/quotes/document): it lives in Storage and is
// only ever read through a signed link that expires in minutes.
// ============================================================================

/** Every quote (the API pages at 500; a CRM this size has far fewer). */
export async function fetchQuotes(): Promise<Quote[]> {
  const page = await fetchCrmObjects("quotes", { limit: 500 });
  return page.rows as unknown as Quote[];
}

export interface QuotePatch {
  amount?: number;
  expiresAt?: string | null;
  sentAt?: string | null;
  status?: string;
  documentUrl?: string | null;
  attachmentId?: string | null;
  attachmentName?: string | null;
  notes?: string | null;
}

export async function updateQuote(id: string, patch: QuotePatch): Promise<void> {
  await updateCrmObject("quotes", id, patch as Record<string, unknown>);
}

/** Attach (or replace) the document of a quote. */
export async function uploadQuoteDocument(id: string, file: File): Promise<NonNullable<Quote["document"]>> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiFormData.post("/crm/quotes/document", form, { params: { id } });
  return (unwrap(res) as { document: NonNullable<Quote["document"]> }).document;
}

/** A link to read the attached document; it stops working after a few minutes. */
export async function fetchQuoteDocumentUrl(id: string): Promise<string> {
  const res = await api.get("/crm/quotes/document", { params: { id } });
  return (unwrap(res) as { url: string }).url;
}

export async function removeQuoteDocument(id: string): Promise<void> {
  await api.delete("/crm/quotes/document", { params: { id } });
}

// ============================================================================
// Armador de cotizaciones (#1545) — API dedicada /crm/quotes. Los totales los
// calcula el servidor; la consola solo los muestra en vivo (quoteMath.ts).
// ============================================================================

export interface QuoteInput {
  title?: string;
  currency?: string;
  expiresAt?: string | null;
  notes?: string | null;
  terms?: string | null;
  dealId?: string | null;
  leadId?: string | null;
  company?: string | null;
  contact?: string | null;
  product?: string | null;
  customer?: Quote["customer"];
  items?: QuoteItem[];
  globalDiscountPct?: number;
}

export async function fetchQuote(id: string): Promise<Quote> {
  return unwrap(await api.get(`/crm/quotes/${encodeURIComponent(id)}`)) as Quote;
}

export async function createQuote(input: QuoteInput): Promise<Quote> {
  return unwrap(await api.post("/crm/quotes", input)) as Quote;
}

export async function saveQuote(id: string, input: QuoteInput): Promise<Quote> {
  return unwrap(await api.put(`/crm/quotes/${encodeURIComponent(id)}`, input)) as Quote;
}

export type QuoteTarget = "enviada" | "aceptada" | "rechazada" | "cancelada";

export async function setQuoteStatus(id: string, status: QuoteTarget, reason?: string): Promise<Quote> {
  return unwrap(await api.post(`/crm/quotes/${encodeURIComponent(id)}/status`, { status, ...(reason ? { reason } : {}) })) as Quote;
}

export async function duplicateQuote(id: string): Promise<Quote> {
  return unwrap(await api.post(`/crm/quotes/${encodeURIComponent(id)}/duplicate`)) as Quote;
}

/** Genera el PDF (queda como documento de la cotización) y devuelve una liga para abrirlo. */
export async function generateQuotePdf(id: string): Promise<{ url: string; document: NonNullable<Quote["document"]> }> {
  return unwrap(await api.post(`/crm/quotes/${encodeURIComponent(id)}/pdf`)) as { url: string; document: NonNullable<Quote["document"]> };
}

export async function sendQuote(id: string, body: { to?: string; message?: string } = {}): Promise<Quote & { sentTo?: string }> {
  return unwrap(await api.post(`/crm/quotes/${encodeURIComponent(id)}/send`, body)) as Quote & { sentTo?: string };
}
